import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRealm } from '../context/RealmContext';
import { useTranslation } from 'react-i18next';

interface RealmTransitionProps {
  children: React.ReactNode;
}

export default function RealmTransition({ children }: RealmTransitionProps) {
  const { currentRealm, isTransitioning } = useRealm();
  const { t } = useTranslation();

  // Enhanced transition durations
  const transitionDuration = 0.4;
  const fadeInDuration = 0.2;

  return (
    <div className="relative">
      <AnimatePresence mode="wait">
        {isTransitioning ? (
          <motion.div
            key="transition"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: fadeInDuration }}
            className="fixed inset-0 z-50 bg-gradient-to-br from-indigo-900/95 via-purple-900/95 to-violet-900/95 backdrop-blur-sm flex flex-col items-center justify-center"
          >
            <div className="relative mb-6">
              {/* Simple spinner */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-12 h-12 border-4 border-t-white border-indigo-300 rounded-full"
              />
            </div>
            
            {/* Enhanced Transition Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="text-center z-10"
            >
              <h2 className="text-white/90 text-2xl font-bold mb-2 tracking-wide">
                {currentRealm === 'outer' ? t('transitions.enteringMemoryWorkspace', { ns: 'common' }) as string : t('transitions.returningToDashboard', { ns: 'common' }) as string}
              </h2>
              
              {/* Simple progress */}
              <motion.div 
                className="mt-8 flex justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                <motion.div 
                  className="h-1 bg-purple-400/30 rounded-full w-40 overflow-hidden"
                >
                  <motion.div 
                    className="h-full bg-gradient-to-r from-indigo-400 to-purple-500"
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: transitionDuration * 0.9, ease: "easeInOut" }}
                  />
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
      
      {/* Main content */}
      {!isTransitioning && children}
    </div>
  );
} 